// @ts-strict-ignore
import React from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { Workflow, WorkflowVersion } from '~/flow/types';
import type { ResponseException } from '~/helpers/exceptionHandler';
import { getMatrixModels } from '~/flow/matrix-models/services';
import apiCalls from '~/helpers/apiCalls';
import { UserContext } from '~/dashboard/context';
import type {
  LegacyAction,
  ServiceCredentials,
  ServiceObject,
  WorkflowPropsResponse,
} from '../store/engine/types';
import { getFormattedPublishedAttributes } from '../../settings/published-attributes/utils';
import type { FeatureFlags } from '~/helpers/typescript';
import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import { routes } from '~/helpers/routes';
import { getCustomListsWithVersions } from '../service/actions/services';
import type { API } from '~/helpers/api-new/modules';
import useAPI from '~/helpers/api-new/context';
import type { Services, WorkflowServiceConfig } from '../store/engine/types/schema';
import { getFormattedAggregationConfig } from '../editor/components/aggregations/helpers';
import { WATCHLIST_DISPOSITION_PA_CATEGORY_KEY } from '../../settings/published-attributes/constants';
import { makeServiceConfig } from '../service/services';

type UseWorkflowPropsOptions = {
  versionId: WorkflowVersion['application_version_id'];
  workflowToken: Workflow['application_token'];
  // Controls whether the workflow props query should automatically execute
  enabled?: boolean;
};

type GetWorkflowPropsOptions = UseWorkflowPropsOptions & {
  /**
   * the library API uses the new api module that is tied to the Context of the application.
   * the `getWorkflowProps` function is not tied to a component or hook lifecycle, so it needs
   * to receive the API as a param. by passing the whole API instead of a single fetcher we
   * add future support for any similar requirements.
   */
  appAPI: API;
  isEmployee: boolean;
  typhonDomain: string;
  canFetchPublishedAttributes: boolean;
  canFetchServiceConfigs: boolean;
  featureFlags: Partial<FeatureFlags>;
};

export async function getWorkflowProps({
  appAPI,
  canFetchPublishedAttributes,
  canFetchServiceConfigs,
  featureFlags,
  isEmployee,
  typhonDomain,
  versionId,
  workflowToken,
}: GetWorkflowPropsOptions): Promise<WorkflowPropsResponse> {
  const [
    baseWorkflowProps,
    // some fields come in the data endpoint, so we must make a dedicated api call to it
    workflowData,
    reasonCodes,
    accountServiceConfigs,
    unformattedPublishedAttributes,
    cloneData,
    customLists = [],
    allLocalAndGlobalSnippets,
    { data_objects: clientQueryDataConfigObjects },
    unformattedUniversalAttributes,
  ] = await Promise.all([
    apiCalls.workflows.getWorkflowProps({ workflowToken }),
    apiCalls.workflows.getWorkflowVersion({
      applicationToken: workflowToken, publicVersion: versionId,
    }),
    apiCalls.settings.getReasonCodes({ typhonDomain }),
    canFetchServiceConfigs && apiCalls.settings.getAccountServiceConfigs({ typhonDomain }),
    canFetchPublishedAttributes && apiCalls.publishedAttributes.list({
      typhonDomain,
      queryParamsStr: buildURLParamsString({
        sysPACategories: JSON.stringify([WATCHLIST_DISPOSITION_PA_CATEGORY_KEY]),
      }),
    }),
    isEmployee && apiCalls.workflows.getCloningData(),
    featureFlags.customListsService && apiCalls.customLists.getAllLists(typhonDomain)
      // If user does not have read Lists permissions, request will return an 403 error
      .catch(() => []),
    featureFlags.ruleLibraryPhase >= 2 && appAPI.library.listAllLocalAndGlobalSnippets(),
    // TODO(OAK-1233): remove decisionOnEverything branch and associated stubbed Promise.resolve once rollout is complete
    featureFlags.decisionOnEverythingFe
      ? apiCalls.clientQueryDataConfig.getObjects({ typhonDomain })
        .catch(() => ({ data_objects: [] }))
      : Promise.resolve({ data_objects: [] }),
    featureFlags.enableErrorAttributes
      && apiCalls.settings.getUniversalAttributes({ typhonDomain }),
  ]);

  const updatedServices: Services = baseWorkflowProps.services;
  /** credentialsSchema is @deprecated - BUILD-4737 to replace */
  const actionPromises = Object.values(workflowData.actions)
    .map(async (action: LegacyAction) => {
      if (action.type === 'run') {
        const serviceData: ServiceObject = Object.values(
          updatedServices as { [id: number]: ServiceObject },
        ).find(
          (service: ServiceObject) => (service.name === action.name));
        if (serviceData) {
          const serviceDataCredSchema = updatedServices[serviceData.id].credentialsSchema;
          if (!serviceDataCredSchema) {
            const serviceCredentialsSchema: ServiceCredentials = await ajaxFetch(
              routes.v3.services(serviceData.internal_name));
            updatedServices[serviceData.id] = {
              ...updatedServices[serviceData.id],
              credentialsSchema: serviceCredentialsSchema,
            };
          }
        }
      }
    });
  await Promise.all(actionPromises);

  const buildServiceConfig = (matchedConfig?: WorkflowServiceConfig): WorkflowServiceConfig => {
    if (matchedConfig) {
      return {
        selectedAlloyOptions: matchedConfig.selectedAlloyOptions,
        selectedCustomerOptions: matchedConfig.selectedCustomerOptions,
        serviceConfigOptions: matchedConfig.serviceConfigOptions,
      };
    }
    const defaultServiceConfig = makeServiceConfig();
    return defaultServiceConfig;
  };

  Object.keys(updatedServices).forEach(serviceId => {
    if (!serviceId) return;
    const matchedConfig = accountServiceConfigs
      ?.services?.find(service => service.serviceId === Number(serviceId));
    updatedServices[serviceId] = {
      ...updatedServices[serviceId],
      serviceConfig: buildServiceConfig(matchedConfig),
    };
  });

  const aggregationConfig = featureFlags.decisionOnEverythingFe
    ? getFormattedAggregationConfig(clientQueryDataConfigObjects)
    : {};


  const matrixModels = await getMatrixModels(typhonDomain, workflowData);

  // need to pull all major versions of each custom list used in the workflow
  const transformedCustomLists = (customLists?.length > 0)
    ? await getCustomListsWithVersions({ typhonDomain, workflowData, customLists }) : [];

  const publishedAttributes = canFetchPublishedAttributes
    ? getFormattedPublishedAttributes(
      unformattedPublishedAttributes.published_attributes,
    )
    : [];

  const reasonCodeGroupsWithCodeData = reasonCodes
    ? baseWorkflowProps.reasonCodeGroups.map(reasonCodeGroup => {
      const codesGroupWithDetails = reasonCodeGroup.reasonCodes.map(reasonCode => {
        const matchingReasonCode = reasonCodes.find(rc => rc.reason_code_id === reasonCode);
        if (matchingReasonCode) {
          return matchingReasonCode;
        }

        return reasonCode;
      });

      return {
        ...reasonCodeGroup,
        reasonCodes: codesGroupWithDetails,
      };
    })
    : baseWorkflowProps.reasonCodeGroups;


  /**
   * If a Service within the reason code group is disabled, the Daffy endpoint will return its
   * service name and the Reason Codes within them as `null`. This transformation filters these
   * `null` values out to avoid having to deal with them throughout our system
   */
  const reasonCodeGroupsWithoutNullData = reasonCodeGroupsWithCodeData.map(rcg => {
    const reasonCodesWithoutNulls = rcg.reasonCodes?.filter(rc => rc !== null) ?? [];
    const serviceNamesWithoutNulls = rcg.services?.filter(service => service !== null) ?? [];

    return {
      archived: rcg.archived,
      id: rcg.id,
      name: rcg.name,
      reasonCodes: reasonCodesWithoutNulls,
      services: serviceNamesWithoutNulls,
    };
  });

  return {
    aggregationConfig,
    allWorkflows: baseWorkflowProps.applications,
    categories: baseWorkflowProps.categories,
    customLists: transformedCustomLists,
    customers: cloneData.customers,
    globalServiceConfigOptions: accountServiceConfigs.globalServiceConfigOptions ?? [],
    importableAttributes: baseWorkflowProps.importableAttributes,
    lists: baseWorkflowProps.lists,
    matrixModels,
    outcomes: workflowData.outcomes,
    publishedAttributes,
    reasonCodeGroups: reasonCodeGroupsWithoutNullData,
    reasonCodes,
    recurringWorkflows: baseWorkflowProps.recurringWorkflows,
    sandboxPersonas: baseWorkflowProps.sandboxPersonas || [],
    services: updatedServices,
    snippets: allLocalAndGlobalSnippets || [],
    tagTypes: baseWorkflowProps.tagTypes || [],
    tags: workflowData.tags,
    universalAttributes: unformattedUniversalAttributes?.universal_attributes || [],
    workflow: baseWorkflowProps.application,
  };
}

function getQueryKey({
  workflowToken,
  versionId,
}: Partial<Pick<UseWorkflowPropsOptions, 'workflowToken' | 'versionId'>> = {}): QueryKey {
  /**
   * if no token is passed, a key that invalidates all workflow props queries is returned,
   * not just the one specific to a token.
   */
  return [
    'workflow.props',
    ...(workflowToken ? [workflowToken] : []),
    ...(versionId ? [versionId] : []),
  ];
}

/**
 * Hook that returns all props bundled together. The existing Daffy BE endpoints
 * send back account-level data such as tags and outcomes over the /data endpoint and
 * other dedicated endpoints such as published attributes. Ideally, that info would be part
 * of the workflow props since these are part of the data that the workflow editor needs
 * to support building policies. Hence, this hook makes all necessary calls and returns a
 * complete props object with the expected data.
 */
function useWorkflowProps(options: UseWorkflowPropsOptions) {
  const {
    agent: { isEmployee }, typhonDomain, featureFlags, permissions,
  } = React.useContext(UserContext);

  const api = useAPI();

  const canFetchPublishedAttributes = Boolean(isEmployee || permissions['READ:PublishedAttributes:null']);
  /** TODO - fix permissions issue BUILD-4645 */
  const canFetchServiceConfigs = Boolean(isEmployee || permissions['READ:CustomerServices:null']);
  const { enabled = true } = options;

  return useQuery<WorkflowPropsResponse, ResponseException>({
    queryKey: getQueryKey(options),
    queryFn: () => getWorkflowProps({
      ...options,
      appAPI: api,
      canFetchPublishedAttributes,
      canFetchServiceConfigs,
      featureFlags,
      isEmployee,
      typhonDomain,
    }),
    retry: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled,
  });
}


useWorkflowProps.getQueryKey = getQueryKey;
export default useWorkflowProps;
